import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, IconButton, LinearProgress, Typography } from '@mui/material';
import { emptyCells } from 'common/constants';
import { IBudget, IBudgetPeriod, IExpense } from 'common/entities';
import { Currency, IMonth, ITableColumn } from 'common/types';
import SortableTable from 'components/Shared/SortableTable';
import { useAppSelector } from 'core/hooks/hooks';
import { useCurrency } from 'core/hooks/useCurrency';
import {
  getCurrentPeriod,
  getMonthPeriod,
  hasPeriod
} from 'core/services/budget.service';
import { sumWithCurrency } from 'core/services/currency.service';
import { getBudgetMonthSpent } from 'core/services/expense.service';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { theme } from 'theme';
import { isFutureMonth } from 'utils/dateUtils';

interface IMonthBudgetRecord {
  id: string;
  name: string;
  progress?: number;
  budgetSum?: number;
  spent?: number;
  balance?: number;
}

export interface BudgetListItemProps {
  budget: IBudget;
  expenses: IExpense[];
  monthDate: Date;
}

export interface MonthlyBudgetExpensesListProps {
  budgets: IBudget[];
  expenses: IExpense[];
}

const budgetColumns: ITableColumn<IMonthBudgetRecord>[] = [
  {
    id: 'name',
    numeric: false,
    label: 'Budget',
    editable: false
  },
  {
    id: 'progress',
    numeric: false,
    label: ''
  },
  {
    id: 'balance',
    numeric: true,
    label: 'Spent/Budget',
    editable: false
  }
];

const createBudgetRow = (
  budget: IBudget | undefined,
  expenses: IExpense[],
  month: IMonth,
  currency: Currency
): IMonthBudgetRecord => {
  const budgetMonth = budget
    ? getMonthPeriod(budget, month) ?? getCurrentPeriod(budget)
    : undefined;
  const spent = getBudgetMonthSpent(budget?.id, expenses, month, currency);
  return {
    id: budget?.id ?? '0',
    name: budget?.name ?? emptyCells.budget,
    budgetSum: budgetMonth?.budget ?? 0,
    progress: budgetMonth?.budget
      ? (spent / (budgetMonth?.budget ?? 1)) * 100
      : undefined,
    spent,
    balance: (budgetMonth?.budget ?? 0) - spent
  };
};

const createRows = (
  budgets: IBudget[],
  expenses: IExpense[],
  monthDate: IMonth,
  currency: Currency
) => {
  return [createBudgetRow(undefined, expenses, monthDate, currency)].concat(
    budgets
      .filter(b => !b.accumulation)
      .map(b => createBudgetRow(b, expenses, monthDate, currency))
  );
};

const AddItemButton = ({ onClick }: { onClick: () => void }) => (
  <IconButton onClick={onClick}>
    <AddCircleOutlinedIcon />
  </IconButton>
);

const MonthBudgetExpensesList = ({
  budgets,
  expenses
}: MonthlyBudgetExpensesListProps) => {
  const budgetMonth = useAppSelector(periodSelector);
  const isFuture = isFutureMonth(budgetMonth);
  const { currency } = useCurrency();
  const monthBudgets = useMemo(
    () =>
      budgets.filter(
        b =>
          !b.accumulation &&
          (hasPeriod(b, budgetMonth) || (!b.single && isFuture))
      ),
    [budgets, budgetMonth, isFuture]
  );

  const budgetRows = useMemo(
    () => createRows(monthBudgets, expenses, budgetMonth, currency),
    [monthBudgets, budgetMonth, expenses, currency]
  );

  const renderBudgetSumItem = (value: any) => <>{sumWithCurrency(value)}</>;

  const createBudgetMonth = useCallback(
    (budgetSum: number | undefined): IBudgetPeriod => ({
      month: budgetMonth.month,
      year: budgetMonth.year,
      budget: budgetSum
    }),
    [budgetMonth]
  );

  const budgetsCustomCellBuilder = useMemo(
    () =>
      (
        column: keyof IMonthBudgetRecord,
        row: IMonthBudgetRecord
      ): JSX.Element => {
        switch (column) {
          case 'name':
            return row.id === '0' ? (
              <Typography
                component={'span'}
                sx={{ color: '#555', fontSize: '12px' }}
              >
                {row.name}
              </Typography>
            ) : (
              <Link
                to={{
                  pathname: `/budget/${row.id}`,
                  state: {
                    goBackTitle: 'Back to Month Budgets'
                  }
                }}
                style={{ color: theme.palette.common.black }}
              >
                {row.name}
              </Link>
            );
          case 'progress':
            return row.progress !== undefined ? (
              <Box
                sx={{
                  ml: '10px',
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center'
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={row.progress}
                  sx={{ height: '1rem', width: '150px' }}
                />
              </Box>
            ) : (
              <></>
            );
          case 'balance':
            return row.id === '0' ? (
              <>-</>
            ) : (
              <>
                {`${sumWithCurrency(row['spent'] ?? 0)} / ${sumWithCurrency(
                  row['budgetSum'] ?? 0
                )}`}
              </>
            );
          default:
            return <>{row[column]}</>;
        }
      },
    [createBudgetMonth]
  );

  return (
    <Box>
      <SortableTable
        columns={budgetColumns}
        rows={budgetRows}
        indexBy="id"
        defaultOrderBy={'name'}
        customCellBuilder={budgetsCustomCellBuilder}
        styles={{
          cell: {
            borderBottom: 'none',
            px: 0,
            py: '.5rem',
            fontSize: 12,
            pb: { md: 0 }
          },
          headCell: { borderBottom: 'none', px: 0, py: '1rem' }
        }}
        getRowExpandedContent={(row: IMonthBudgetRecord) => (
          <Box
            sx={{ width: '100%', display: { xs: 'block', md: 'none' }, mb: 2 }}
          >
            {row.progress !== undefined ? (
              <LinearProgress
                variant="determinate"
                value={row.progress}
                sx={{ height: '1rem' }}
              />
            ) : null}
          </Box>
        )}
        showHeader={false}
      />
    </Box>
  );
};

export default MonthBudgetExpensesList;
